import {
  Card,
  CardActionArea,
  CardContent,
  Divider,
  Stack,
  Typography,
} from '@mui/material'
import { Icons } from 'utoniq-ui'

import { EventTicketCardFragment } from '@/clients/utoniq-core/schema'
import { EventCategory, ThroughTicketTag } from '@/components/organisms'
import { styleSets } from '@/components/styles'
import { useRouter } from '@/core'
import { Organism } from '@/core/architecture'
import { encodeSkuId } from '@/core/sku'

type MyPreSaleItem = Exclude<
  EventTicketCardFragment['myPreSaleItems'],
  null | undefined
>[number]

type EventTicketCardProps = {
  item: MyPreSaleItem['item']
  skuId?: MyPreSaleItem['skuId']
  event: EventTicketCardFragment
  width?: number
}

export const EventTicketCard: Organism<EventTicketCardProps> = ({
  item,
  event,
  skuId,
  width,
}) => {
  const { generateQueryPath, routerPages } = useRouter()
  return (
    <Card sx={{ borderRadius: 2, width }}>
      <CardActionArea
        sx={{ height: '100%' }}
        href={generateQueryPath(
          routerPages.OwnedItemDetail,
          { itemId: item.id },
          { skuId: encodeSkuId(skuId ?? '') }
        )}
      >
        <CardContent sx={{ p: 4, '&:last-child': { pb: 4 } }}>
          <EventCategory event={event} />
          <Stack py={2} spacing={1}>
            <Typography variant="subtitle1" sx={styleSets.lineCamp(2)}>
              {item.title}
            </Typography>
            {(!!item.sku?.unitName ||
              item.metadata?.ticket?.isThroughTicket) && (
              <Stack direction="row" spacing={2} alignItems="center">
                {!!item.sku?.unitName && (
                  <Typography
                    variant="caption"
                    sx={{
                      ...styleSets.lineCamp(1),
                      maxWidth: 200,
                      lineHeight: '20px',
                    }}
                  >
                    {item.sku.unitName}
                  </Typography>
                )}
                {item.metadata?.ticket?.isThroughTicket && <ThroughTicketTag />}
              </Stack>
            )}
          </Stack>
          <Divider />
          <Stack pt={2}>
            <Typography variant="subtitle2" sx={styleSets.lineCamp(2)}>
              {event.title}
            </Typography>
            <Stack
              direction="row"
              spacing={1}
              alignItems="center"
              sx={{ pt: 2 }}
            >
              <Icons.Calendar fontSize="small" sx={{ color: 'grey.700' }} />
              <Typography variant="overline" color="text.secondary">
                {event.dateRange}
              </Typography>
            </Stack>
            <Stack
              direction="row"
              spacing={1}
              alignItems="center"
              sx={{ pt: 1 }}
            >
              <Icons.Pin fontSize="small" sx={{ color: 'grey.700' }} />
              <Typography
                variant="overline"
                color="text.secondary"
                sx={styleSets.lineCamp(1)}
              >
                {event.location?.approximateAddress}
              </Typography>
            </Stack>
          </Stack>
        </CardContent>
      </CardActionArea>
    </Card>
  )
}
